import * as React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import Heading from '~components/Heading';
import Text from '~components/Text';

import { fsbgmedia, quoteImgStyle } from '~theme';
import QuoteBgInverse from '~components/Icon/QuoteBgInverse';
import QuoteBg from '~components/Icon/QuoteBg';
import FullscreenVideo from '~components/FullscreenVideo';

import safePlaceWebm from '../videos/safe-place.webm';
import safePlaceMp4 from '../videos/safe-place.mp4';

const SafePlacePage: React.FC = () => (
  <Layout>
    <SEO title="Home: A Safe Place to Receive Care" desc="" />
    <FullscreenVideo
      src={{
        webm: safePlaceWebm,
        mp4: safePlaceMp4
      }}
    >
      <div tw="bg-gray-100 bg-opacity-95 pt-12">
        <div tw="grid lg:(grid-cols-2 gap-12)">
          <div tw="flex flex-col items-center justify-center lg:(col-start-2 row-start-1)">
            <div tw="px-6 lg:px-12">
              <Heading tw="leading-tight my-0" level="h1">
                Home: A Safe Place to Receive Care
              </Heading>
            </div>
          </div>
          <div tw="lg:(col-start-1 row-start-1)">
            <StaticImage src="../images/christina-hero.png" alt="" />
          </div>
        </div>
      </div>
    </FullscreenVideo>
    <div tw="bg-gray-100 py-12">
      <div tw="container">
        <Heading level="h2">
        Paediatric Nurse Christina D'Orazio is Helping Families Through Unprecedented Times
        </Heading>
        <p tw="text-base md:text-xl my-4">
        Families with young children, particularly those whose children have complex medical needs, have faced significant uncertainty throughout the COVID-19 pandemic. Home care specialists like Paediatric Nurse Christina D’Orazio have supported these families throughout, making it possible for VHA’s young clients to attend school in person when possible and to receive care safely at home.
        </p>
        <div tw="md:my-12 grid gap-4 lg:grid-cols-3">
          <div tw="relative lg:col-span-1">
            <StaticImage src="../images/christina-in-line.jpg" alt="" css={fsbgmedia} />
          </div>
          <div tw="lg:col-span-2">
            <div tw="bg-gray-900 text-white p-6">
              <div tw="w-full border-b-2 border-white relative mb-10 mt-6">
                <QuoteBgInverse tw="absolute left-1/2 h-24 w-24 transform -translate-x-1/2 -translate-y-1/2 text-gray-900" />
              </div>
              <p tw="text-base md:text-xl md:my-4">
              “Every wave of the pandemic has caused anxiety for parents. And with the shortage of health care workers, it’s an added stress when they aren’t sure if their family will have the same nurse, or any nurse at all, to support their child at school when in-person learning is available. Parents are thrilled that someone is able to be there to keep their child safe and healthy and I’m glad to be able to bring some comfort so they can go to work to support their family and not have to worry.”
              </p>
            </div>
          </div>
        </div>
        <p tw="text-base md:text-xl my-4">
        Christina has been a nurse for nearly 30 years and has dedicated the majority of her career to working in home and community care. She has had the opportunity to develop the knowledge and skills to address the needs of a wide range of clients, but specializes in IV therapy, diabetic care and palliative care.
        </p>
        <p tw="text-base md:text-xl my-4">
        A challenging, but very rewarding part of Christina’s job is seeing young palliative clients and helping families through end-of-life treatment.
        </p>
        <p tw="text-base md:text-xl my-4">
        “I put everything I have into helping palliative clients,” says Christina. “It’s such a difficult time for parents, and if I can have a therapeutic effect and make it even a little bit easier, I’ve done my job.”
        </p>
        <Heading level="h2">VHA Teams Bring COVID-19 Vaccines to Those Who Need Them Most</Heading>
        <p tw="text-base md:text-xl my-4">
        Over the past year, millions of Ontarians have enthusiastically booked appointments or lined up to receive first, second and third doses of COVID-19 vaccines to protect themselves and their communities from infection and serious illness.
        </p>
        <p tw="text-base md:text-xl my-4">
        But for some, accessing the vaccine has not been easy. Some of our most vulnerable residents, particularly in urban centres like Toronto, have experienced barriers including mobility and technology. To address this, VHA team members implemented innovative approaches to get vaccines to those with the greatest need.  
        </p>
        <div tw="grid gap-12 my-12 lg:(grid-cols-2)">
          <div>
            <StaticImage src="../images/distancing-high-five.jpg" alt="" />
          </div>
          <div>
            <StaticImage src="../images/receiving-vaccine.jpg" alt="" />
          </div>
        </div>
        <p tw="text-base md:text-xl my-4">
        House Calls is a partnership program between VHA and SPRINT Senior Care that has been supporting homebound seniors with physical, cognitive and social frailties for over twelve years by providing primary medical care, social work and rehab therapies in their homes. “We began advocating for homebound seniors to be vaccinated in their homes as soon as COVID-19 vaccines were developed. For our patients and many others like them across the province, in-home vaccination is not a convenience. It is a necessity and a life-saving intervention,” says Leslie Coulter, VHA Occupational Therapist and Associate Program Lead of House Calls. “While some seniors with complex health issues can benefit from transportation services to vaccination clinics, without in-home programs the majority of our clients simply would not have gotten vaccinated.”
        </p>
      </div>
      <div tw="w-full my-6 md:my-12">
        <StaticImage src="../images/volunteers-raising-arms.jpg" alt="" layout="fullWidth" />
      </div>
      <div tw="container">
        <p tw="text-base md:text-xl my-4">
        To further support COVID-19 vaccination for other parts of our community, VHA's Tactical HealthCare Response Unit (THRU), came together during the third wave. THRU is a strategic team consisting of nurses, pharmacists, pharmacy technicians and non-clinical staff, all with huge hearts, who can urgently respond to community health needs and challenges anywhere in the Greater Toronto Area. Throughout 2021 to present day, the team worked with partners on countless vaccination initiatives, including running a mass vaccine clinic in the North-West region of Toronto, creative pop-up clinics and assisting with homebound vaccinations across the Greater Toronto Area. THRU continues to run the mass vaccine clinic in addition to a testing centre in the same building as part of Team Toronto’s efforts to increase vaccination rates in this region which was a COVID-19 hotspot with one of the lowest rates of vaccination in the city in 2021.
        </p>
        <p tw="text-base md:text-xl my-4">
        “VHA’s THRU is proud to be part of this important work to ensure everyone who wants a COVID-19 vaccine can easily access their shots within their community,” says Susan Chang, Director, Health System Transformation.  “It was great to see that most Torontonians took the opportunity to begin the COVID-19 vaccination process. But until we were able to remove barriers for all Torontonians to make an informed decision, we as a health system hadn’t done our job.”
        </p>
      </div>
    </div>
  </Layout>
);

export default SafePlacePage;
